import { apiConfig } from './api/apiConfig';

export interface AnalysisData {
  executive_summary?: {
    overview: string;
    overall_favorability_score: number;
    key_concerns: string[];
    recommendations: string[];
  };
  clauses?: Array<{
    id: string;
    title: string;
    content: string;
    analysis: string;
    category: string;
    subcategory: string;
    risk_level: 'low' | 'medium' | 'high';
    negotiation_status: 'pending' | 'agreed' | 'rejected' | 'compromised';
    party_obligations: string;
    summary: string;
  }>;
  negotiation_points?: Array<{
    id: string;
    category: string;
    point: string;
    priority: 'low' | 'medium' | 'high';
    status: 'pending' | 'agreed' | 'rejected' | 'compromised';
    supplier_position?: string;
    our_position?: string;
    compromise?: string;
    notes?: string;
  }>;
  risk_assessment?: {
    overall_risk: 'low' | 'medium' | 'high';
    risk_factors: string[];
    recommendations: string[];
  };
}

export interface AnalysisResponse {
  success: boolean;
  message?: string;
  data?: {
    _id: string;
    contractId: string;
    analysisData: AnalysisData;
    fileName: string;
    uploadedBy: string;
    analysisDate: string;
    version: number;
    createdAt: string;
    updatedAt: string;
  };
}

class AnalysisService {
  private getAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return {
      'Content-Type': 'application/json',
      'Authorization': token ? `Bearer ${token}` : '',
    };
  }

  async saveAnalysis(contractId: string, analysisData: AnalysisData, fileName: string): Promise<AnalysisResponse> {
    const response = await fetch(`${apiConfig.baseURL}/api/contracts/${contractId}/analyze`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({
        analysisData,
        fileName
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to save analysis');
    }

    return data;
  }

  async getAnalysis(contractId: string): Promise<AnalysisResponse> {
    const response = await fetch(`${apiConfig.baseURL}/api/contracts/${contractId}/analysis`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to retrieve analysis');
    }

    return data;
  }

  async checkAnalysisExists(contractId: string): Promise<{ success: boolean; exists: boolean }> {
    const response = await fetch(`${apiConfig.baseURL}/api/contracts/${contractId}/analysis/exists`, {
      method: 'GET',
      headers: this.getAuthHeaders(),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to check analysis existence');
    }

    return data;
  }

  async deleteAnalysis(contractId: string): Promise<{ success: boolean; message: string; deletedCount: number }> {
    const response = await fetch(`${apiConfig.baseURL}/api/contracts/${contractId}/analysis`, {
      method: 'DELETE',
      headers: this.getAuthHeaders(),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to delete analysis');
    }

    return data;
  }
}

export const analysisService = new AnalysisService();
export default analysisService;